<template>
    <div :id="divName" ></div>
</template>

<script setup>
// @ts-nocheck
  import {ref, onMounted, watch, toRefs, computed } from "vue";
  import * as d3 from 'd3';

  const props = defineProps({
    divName:{type: String},
    categoryProp: { type: String }, // the data object prop that defines the categories for each bar (e.g. age)
    valueProp: { type: String }, // the data object prop that defines the values for each bar (e.g. population)
    barName: { type: String }, // the data object prop that defines the name of the different bars (e.g. state)
    // colors:{type:Object},
    data:{type:Array},
    sortCategories: {type:Array, default: null},
    categoryColors : {type: Object, default: null},
    xAxisTicks : {type: Boolean, default: true},
    topMargin: {type: Number, default: 25},
    leftMargin: {type: Number, default: 235},
    rectOutline: {type: Boolean, default: false},
    labelFontSize:{type:Number, default: 18},
    barWidth:{type:Number, default: 600},
    tooltipTitleProp: { type: String, default: null },
    tooltipCategoryProp: { type: String, default: null },
    tooltipValueSuffix: { type: String, default: '%' },
    yAxisTicks: { type: Boolean, default: true }
  })

  const { barWidth, labelFontSize, divName, data, barName, valueProp, categoryProp, sortCategories, categoryColors, xAxisTicks, yAxisTicks, topMargin, leftMargin, rectOutline, tooltipTitleProp, tooltipCategoryProp, tooltipValueSuffix } = toRefs(props)

  // console.log(data)

  const width = barWidth.value;
  const marginTop = topMargin.value
  const marginRight = 30;
  const marginBottom = 0;
  const marginLeft = leftMargin.value;

  

  onMounted(() => {
    updateChart()
  })

  watch(data, (newVal, oldVal)=>{
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)){
      updateChart()
    }
  })


  const updateChart = () => {


    const chartSelector = divName.value;
    d3.select("#" + chartSelector).select("svg").remove();
    // const colorsObj = colors.value;

    // used to sort the bars.. e.g. the one with the most "Very High" on top.  Also "Very High" on the left
    const categories = sortCategories.value;

    // Determine the series that need to be stacked.
    // if there are categories passed in, put the first value on the left side of the bars (e.g. Very High")
    let series
    if (categories){
      series = d3.stack()
        .keys(categories) // Use the predefined order of categories
        .value(([, D], key) => D.get(key)[valueProp.value]) // get value for each series key and stack
        (d3.index(data.value, d => d[barName.value], d => d[categoryProp.value])); // group by stack then series key
    }
    else{
      series = d3.stack()
        .keys(d3.union(data.value.map(d => d[categoryProp.value]))) // distinct series keys, in input order
        .value(([, D], key) => D.get(key)[valueProp.value]) // get value for each series key and stack
        (d3.index(data.value, d => d[barName.value], d => d[categoryProp.value])); // group by stack then series key
    }




    // Compute the height from the number of stacks.
    const height = series[0].length * 25 + marginTop + marginBottom;



    // Sort the data based on multiple categories
    const sortByCategories = (data, categories) => {
      return data.sort((a, b) => {
        for (let category of categories) {
          const aValue = data.filter(d => d.sp === a.sp && d.category === category).map(d => d.value)[0] || 0;
          const bValue = data.filter(d => d.sp === b.sp && d.category === category).map(d => d.value)[0] || 0;
          if (bValue !== aValue) {
            return bValue - aValue;
          }
        }
        return 0;
      });
    };

    
    let sortedData
    if (sortCategories.value){
      sortedData = sortByCategories(data.value, categories);
    }
    else{
      sortedData = data.value
    }




    // Prepare the scales for positional and color encodings.
    const x = d3.scaleLinear()
      .domain([0, d3.max(series, d => d3.max(d, d => d[1]))])
      .range([marginLeft, width - marginRight]);

    const y = d3.scaleBand()
      // .domain(d3.groupSort(data.value, D => -d3.sum(D, d => d[valueProp.value]), d => d[barName.value]))
      .domain(sortedData.map(d => d[barName.value]))
      .range([marginTop, height - marginBottom])
      .padding(0.08);

    // this is noly if a color object isn't passed in
    let color
    try{
      color = d3.scaleOrdinal()
        .domain(series.map(d => d.key))
        .range(d3.schemeSpectral[series.length])
        .unknown("#ccc");
    }
    catch{
      color = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
    }




    // A function to format the value in the tooltip.
    const formatValue = x => isNaN(x) ? "N/A" : x.toLocaleString("en")

    // Create the SVG container.
    const svg = d3.select('#' + chartSelector)
      .append('div')
      .classed("svg-container", true)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width: 100%; height: auto;");

    // Append a group for each series, and a rect for each element in the series.
    svg.append("g")
      .selectAll()
      .data(series)
      .join("g")
      .attr("fill", d => {
        if (!categoryColors){
          return color(d.key)
        }
        else{
          return categoryColors.value[d.key]
        }
      })
      .attr("style", d => {
        if(rectOutline.value){

          return "outline: 1px solid black; z-index: 100;"
        }
        
      }) 
      
        

      .selectAll("rect")
      .data(D => D.map(d => (d.key = D.key, d)))
      .join("rect")
      .attr("x", d => x(d[0]))
      .attr("y", d => {
        if(d.data[0]){
          y(d.data[0])
        }
      })
      .attr("height", y.bandwidth())
      .attr("width", d => x(d[1]) - x(d[0]))
      // .append("title")
      .text(d => `${d.data[0]} ${d.key}\n${formatValue(d.data[1].get(d.key)[valueProp.value])}`);

    // Append the horizontal axis.
    if (xAxisTicks.value){
      svg.append("g")
        .attr("transform", `translate(0,${marginTop})`)
        .call(d3.axisTop(x).ticks(width / 100, "s"))
        .call(g => g.selectAll(".domain").remove());
    }
    else{
      svg.append("g")
        .attr("transform", `translate(0,${marginTop})`)
        .call(g => g.selectAll(".domain").remove());
    }


    // Append the vertical axis.
    const yAxis = d3.axisLeft(y)
      .tickSizeOuter(0)
      .tickSize(yAxisTicks.value ? 0 : 0)
      .tickFormat(yAxisTicks.value ? null : () => "")

    const yAxisGroup = svg.append("g")
      .attr("transform", `translate(${marginLeft},0)`)
      .call(yAxis)
      .call(g => g.selectAll(".domain").remove())

    if (yAxisTicks.value) {
      yAxisGroup
        .selectAll("text")
        .style("font-size", labelFontSize.value + "px")
    }
      // function to wrap y-axis labels, but a pain because would need to 
      // change bar row height to make it work.  Instead, shortened names
    // svg.append("g")
    //   .attr("transform", `translate(${marginLeft},0)`)
    //   .call(d3.axisLeft(y).tickSizeOuter(0))
    //   .call(g => g.selectAll(".domain").remove())
    //   .selectAll("text")
    //   .style("font-size", "18px")
    //   .style("text-anchor", "start") // Align text to the start (left)
    //   .each(function() {
    //     var text = d3.select(this),
    //         words = text.text().split(/\s+/).reverse(),
    //         word,
    //         line = [],
    //         lineNumber = 0,
    //         lineHeight = 1.1, // ems
    //         y = text.attr("y"),
    //         dy = parseFloat(text.attr("dy")),
    //         tspan = text.text(null).append("tspan").attr("x", 0).attr("y", y).attr("dy", dy + "em"),
    //         maxWidth = 100; // Set the maximum width for the labels
        
    //     while (word = words.pop()) {
    //       line.push(word);
    //       tspan.text(line.join(" "));
    //       if (tspan.node().getComputedTextLength() > maxWidth) {
    //         line.pop();
    //         tspan.text(line.join(" "));
    //         line = [word];
    //         tspan = text.append("tspan")
    //                     .attr("x", 0)
    //                     .attr("y", y)
    //                     .attr("dy", ++lineNumber * lineHeight + dy + "em")
    //                     .text(word);
    //       }
    //     }
    //   })
    //   .raise(); // Bring the text elements to the front


    let tooltip = d3.select("body")
      .append("div")
      .style("position", "absolute")
      .style("z-index", "10")
      .style("visibility", "hidden")
      .attr("class", "tooltip")
      .style("background-color", "#ffffff")
      .style("border", "solid")
      .style("border-width", "1px")
      .style("border-radius", "5px")
      .style("padding", "3px")
      .style("font-family", "sans-serif")

      svg.selectAll("g")
        .on("mouseover", function(e){
          // console.log(e)
          let v = e.target.__data__
          if (v.data){
            const tooltipData = v.data[1].get(v.key)
            const title = tooltipTitleProp.value ? tooltipData?.[tooltipTitleProp.value] : v.data[0]
            const categoryLabel = tooltipCategoryProp.value ? tooltipData?.[tooltipCategoryProp.value] : v.key
            const value = tooltipData?.[valueProp.value]
            let ttip
            if (title){ttip = "<b>" + title + "</b><br/> " + categoryLabel + " " + value + tooltipValueSuffix.value}
            else{ttip = categoryLabel + " " + value + tooltipValueSuffix.value}
            tooltip
            .html( ttip) 
            .style("opacity", 1)
            .style("font", "14px sans-serif")
            .style("z-index", 9999)
            return tooltip.style("visibility", "visible");
          }

        })
        .on("mousemove", function(e){return tooltip
          .style("top", (e.pageY -15)+"px")
          .style("left",(e.pageX +12)+"px")

          ;})
        .on("mouseout", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("wheel", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousezoomin.zoom", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousezoomout.zoom", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousewheel.zoom", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mouseup", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousewheel", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousezoomout", function(){return tooltip
          .style("visibility", "hidden")
        ;})
        .on("mousezoomin", function(){return tooltip
          .style("visibility", "hidden")
        ;})
  }
  const wrap = (text, width) => {
    text.each(function() {
      var text = d3.select(this),
          words = text.text().split(/\s+/).reverse(),
          word,
          line = [],
          lineNumber = 0,
          lineHeight = 1.1, // ems
          y = text.attr("y"),
          dy = parseFloat(text.attr("dy")),
          tspan = text.text(null).append("tspan").attr("x", 0).attr("y", y).attr("dy", dy + "em");
          // console.log(text)
      while (word = words.pop()) {
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];
          tspan = text.append("tspan").attr("x", 0).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
        }
      }
    });
  }
</script>
<style scoped>
.svg-container{
  max-height: 15px;
}
</style>
